/**
 * Leaving for a provider's payment page, and coming back.
 *
 * On the web the donor goes to Stripe or JazzCash in the same tab and returns
 * to `/payment/return?paymentId=…` under the app's base path (so
 * `/app/payment/return` where the app shares a domain with the company site),
 * which reopens the waiting screen. The
 * Android app cannot be returned to by a web page, so it opens the page in
 * the browser and keeps asking the server in the meantime; the browser lands
 * on the website's return page, and the app has usually noticed the payment
 * before the donor switches back.
 */

import { Linking, Platform } from 'react-native';

import { config } from '../../app/config';

const RETURN_PATH = '/payment/return';

/** The return page's path on the web, under the base path the app is served from. */
const webReturnPath = (): string => `${config.basePath.replace(/\/$/, '')}${RETURN_PATH}`;

/** Where the provider sends the donor after paying. */
export function paymentReturnUrl(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return `${window.location.origin}${webReturnPath()}`;
  }
  // The deployed website: a friendly page for a donor who paid in a browser.
  return `${config.apiBaseUrl.replace(/\/api\/v1$/, '')}${RETURN_PATH}`;
}

export async function openHostedPage(url: string): Promise<void> {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.assign(url);
    return;
  }
  await Linking.openURL(url);
}

export interface PaymentReturn {
  paymentId: string;
  cancelled: boolean;
}

/**
 * A donor arriving back from a hosted page, if that is how the site was
 * opened. The address is cleared afterwards, so a reload lands on Home rather
 * than replaying the return.
 */
export function consumePaymentReturn(): PaymentReturn | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  if (window.location.pathname !== webReturnPath()) return null;
  const params = new URLSearchParams(window.location.search);
  const paymentId = params.get('paymentId');
  window.history.replaceState(null, '', config.basePath);
  if (!paymentId) return null;
  return { paymentId, cancelled: params.get('result') === 'cancelled' };
}
